import { INamedEntity } from './general';
import { Status } from './resource';

export interface ITodayWidgetStatistics {
  total: number;
  lastActivity: string;
}

export interface IWorkerStatistics {
  sent: number;
  expired: number;
  active: number;
  migrated: number;
  pendingRegistration: number;
}

export interface IInventoryStatistics {
  assignedDevices: number;
  availableAccessControlSystems: number;
  availableBadgePrintingSystems: number;
}

export interface IResourceStatistics {
  draft: number;
  pendingApproval: number;
  active: number;
}

export interface IProjectStatistics extends IResourceStatistics {
  pending?: number;
  billing?: number;
  accepted?: number;
}

export interface IClientDetailStatistics {
  projects?: number;
  workers?: number;
  revenue?: number;
}

export interface IProjectDetailStatistics {
  companiesCount?: number;
  activeWorkersCount?: number;
  totalBilling?: number;
  activeBadgeWorkersCount?: number;
}

export interface IPieChartData {
  status?: Status;
  segment?: number;
  total: number;
  percentage: number;
  color?: string;
  label?: string;
}

export interface ILineChartData {
  date: string;
  count: number;
}

export interface ILinePieWidgetStatistics {
  newProjects?: number;
  totalRecords?: number;
  lineChart: ILineChartData[];
  pieChart: IPieChartData[];
}

export interface IPieWidgetStatistics {
  newClients?: number;
  totalRecords?: number;
  chart?: IPieChartData[];
  pieChart?: IPieChartData[];
}

export interface ILineWidgetStatistics {
  totalRevenue?: number;
  totalRecords?: number;
  lineChart: ILineChartData[];
}

export interface IInvoiceStatistics {
  unpaid: number;
  paid: number;
  revenue: number;
}

export interface IPoint {
  entityName?: INamedEntity;
  latitude: number;
  longitude: number;
}

export interface ILocationStatistics {
  total: number;
  pointList: IPoint[];
}

export interface ITopTenStatistics {
  description: string;
  percentage: number;
  total: number;
}

export interface IDeviceStatistics {
  totalRecords: number;
  totalFilter: number;
}

export interface IProjectTopTenStatistics {
  project: INamedEntity;
  totalClients: number;
  totalWorkers: number;
  totalRevenue: number;
  creationDate: string;
}

export interface IClientTopTenStatistics {
  company: INamedEntity;
  projectsCount: number;
  workersCount: number;
  activeBadgesCount: number;
  totalRevenue: number;
}

export interface IAcsSummaryStatistics extends IDeviceStatistics {
  pieChart: IPieChartData[];
}

export interface ILaborCensus {
  totalWorkers: number;
}

export interface IDashboardFilter {
  name: string;
  parameters: {
    companyList: any[];
    projectList: any[];
    startDate: string | null;
    endDate: string | null;
    stateCode?: string;
    countryCode?: string;
    period?: number;
    timeZoneOffset?: string;
    filterOrder: string[];
    countries: any[];
    states: any[];
    periodFilter?: any;
    selectedCompanyIds?: string[];
    selectedProjectIds?: string[];
  };
}

export interface IDashboardProject {
  companies: { id: string; name: string }[];
  createdDate: string;
  endDate: string;
  projectCountry: string;
  projectId: string;
  projectName: string;
  projectState: string;
  projectStateCode: string;
  projectStatus: number;
  startDate: string;
}

export interface IWorkForceMonitoringData {
  totalCompanies: number;
  totalTrades: number;
  totalWorkers: number;
}

export interface IDailyLogData {
  totalCompanies: number;
  totalTrades: number;
  totalWorkers: number;
  newCompaniesEnrolled: number;
  newWorkersBadged: number;
  totalOnSiteHours: number;
}

export interface IExpandedWorkerOnsite {
  id: string;
  name?: string;
  lastName: string;
  firstName: string;
  hours: string;
  lastActivity: string;
  isOnSite: boolean;
}

export interface IExpandedWorkersOnsiteResponse {
  pageNumber: number;
  pageSize: number;
  totalResults: number;
  items: IExpandedWorkerOnsite[];
}
export interface IExpandedCompanyOnsite {
  companyId: string;
  name: string;
  headCount: number;
  hours: string;
  workersData?: IExpandedWorkerOnsite[];
}

export interface IExpandedCompaniesOnsiteResponse {
  pageNumber: number;
  pageSize: number;
  totalResults: number;
  items: IExpandedCompanyOnsite[];
}

export interface IExpandedTradeOnsite {
  projectSkilledTradeId: string;
  name: string;
  headCount: number;
  hours: string;
}

export interface IExpandedTradesOnsiteResponse {
  pageNumber: number;
  pageSize: number;
  totalResults: number;
  items: IExpandedTradeOnsite[];
}

export enum ExportFormatType {
  EXCEL = 0,
  PDF = 1,
}

export enum ExportType {
  OnSiteCompany = 0,
  OnSiteTrade = 1,
  OnSiteWorker = 2,
  DailyLogTotalCompany = 3,
  DailyLogTotalTrade = 4,
  DailyLogTotalWorker = 5,
  DailyLogNewWorker = 6,
  DailyLogNewCompany = 7,
  DailyLogTotalHours = 8,
}
export interface IExportRequest {
  formatType: ExportFormatType;
  type: ExportType;
  filterCriteria?: string;
}

export const exportTypeMapper = {
  [ExportType.OnSiteCompany]: 'Companies Onsite',
  [ExportType.OnSiteTrade]: 'Trades Onsite',
  [ExportType.OnSiteWorker]: 'Workers Onsite',
  [ExportType.DailyLogTotalCompany]: 'Total Companies',
  [ExportType.DailyLogTotalTrade]: 'Total Trades',
  [ExportType.DailyLogTotalWorker]: 'Total Headcount',
  [ExportType.DailyLogNewWorker]: 'New Workers Badged',
  [ExportType.DailyLogNewCompany]: 'New Companies Enrolled',
  [ExportType.DailyLogTotalHours]: 'Total Hours',
};

export const getLinePieWidgetFallback = (): ILinePieWidgetStatistics => ({
  newProjects: 0,
  totalRecords: 0,
  lineChart: [],
  pieChart: [],
});

export const getLineWidgetFallback = (): ILineWidgetStatistics => ({
  totalRevenue: 0,
  totalRecords: 0,
  lineChart: [],
});

export const getPieWidgetStatisticsFallback = (): IPieWidgetStatistics => ({
  newClients: 0,
  totalRecords: 0,
  chart: [],
  pieChart: [],
});

export const getAcsSummaryFallback = (): IAcsSummaryStatistics => ({
  totalRecords: 0,
  totalFilter: 0,
  pieChart: [],
});

export const getLocationFallback = (): ILocationStatistics => ({
  total: 0,
  pointList: [],
});
